// OWNER: AREA-EVENTS (event-types/*, messages/*, requests/*). Components go in src/features/events.
// Send request detail: what the caller sent, what MailSmith answered, and the messages it produced.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Archive, Inbox, Mail } from 'lucide-react'
import { useMemo } from 'react'
import { asApiError } from '@/api/errors'
import { usePermissions } from '@/auth/usePermissions'
import { IdText } from '@/components/CopyButton'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { JsonViewer } from '@/components/JsonViewer'
import { KeyValueList } from '@/components/KeyValueList'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState } from '@/components/QueryState'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Skeleton, SkeletonText } from '@/components/ui/skeleton'
import { applicationQuery } from '@/api/queries/applications'
import { requestQuery } from '@/features/events/api'
import { apiKeysQuery } from '@/features/ops/api'
import { messageColumns } from '@/features/events/messages/columns'
import type { RequestKind, SendRequestDetail } from '@/features/events/types'
import { formatDateTime } from '@/lib/format'
import { paramCrumb } from '@/shell/crumbs'
import { ModeBadge } from '@/components/ModeBadge'

export const Route = createFileRoute('/requests/$requestId')({
  staticData: { crumb: paramCrumb('requestId') },
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(requestQuery(params.requestId))
  },
  component: RequestDetailPage,
})

const KIND_LABELS: Record<RequestKind, string> = {
  event: 'Event',
  direct: 'Direct send',
  resend: 'Resend',
  test: 'Test send',
}

const columns = messageColumns({ hideEvent: true })

function RequestDetailPage() {
  const { requestId } = Route.useParams()
  const navigate = useNavigate()
  const q = useQuery(requestQuery(requestId))

  if (q.isPending)
    return (
      <div aria-busy>
        <Skeleton className="h-7 w-80" />
        <Skeleton className="mt-3 h-4 w-64" />
        <div className="mt-8 grid gap-5 lg:grid-cols-2">
          <SkeletonText lines={6} />
          <Skeleton className="h-60 w-full" />
        </div>
      </div>
    )
  if (q.error)
    return asApiError(q.error).isNotFound ? (
      <EmptyState
        icon={Inbox}
        title="This request doesn’t exist"
        description="Requests are kept for the application’s retention window. It may have expired, or the link is out of date."
        action={
          <Button asChild>
            <Link to="/messages">Go to messages</Link>
          </Button>
        }
      />
    ) : (
      <ErrorState error={q.error} onRetry={() => void q.refetch()} />
    )

  const r = q.data
  const title = r.event_key ? (
    <span className="font-mono text-[0.9em]">{r.event_key}</span>
  ) : (
    KIND_LABELS[r.kind]
  )
  return (
    <>
      <PageHeader
        title={title}
        description="The request as the caller sent it, the response MailSmith returned, and the messages it created."
        meta={
          <>
            <Badge tone={r.kind === 'event' ? 'neutral' : 'info'}>{KIND_LABELS[r.kind]}</Badge>
            {r.test && <ModeBadge mode="test" />}
            {r.response_status != null && <ResponseStatus status={r.response_status} />}
            <span>
              Received <RelativeTime value={r.created_at} />
            </span>
            <IdText id={r.id} />
          </>
        }
      />

      <div className="space-y-5">
        <section aria-labelledby="request-messages" className="space-y-2">
          <h2 id="request-messages" className="text-sm font-semibold text-ink">
            Messages <span className="tabular font-normal text-ink-3">{r.messages.length}</span>
          </h2>
          <DataTable
            aria-label="Messages from this request"
            density="compact"
            columns={columns}
            data={r.messages}
            getRowId={(m) => m.id}
            columnToggle={false}
            onRowClick={(m) =>
              void navigate({ to: '/messages/$messageId', params: { messageId: m.id } })
            }
            empty={
              <EmptyState
                compact
                icon={Mail}
                title="No messages"
                description={
                  r.response_status != null && r.response_status >= 400
                    ? 'The request was rejected, so nothing was sent. See the response below.'
                    : 'No rule matched, or every recipient was suppressed.'
                }
              />
            }
          />
        </section>

        <div className="grid gap-5 lg:grid-cols-[22rem_minmax(0,1fr)]">
          <Card className="self-start">
            <CardHeader title="Request" />
            <CardBody>
              <RequestInfo request={r} />
            </CardBody>
          </Card>
          <div className="min-w-0 space-y-5">
            <PayloadCard request={r} />
            {r.response != null && (
              <Card>
                <CardHeader
                  title="Response"
                  description="What MailSmith returned to the caller. Idempotent retries replay it."
                  actions={
                    r.response_status != null && <ResponseStatus status={r.response_status} />
                  }
                />
                <CardBody>
                  <JsonViewer value={r.response} defaultExpandDepth={3} maxHeight={420} />
                </CardBody>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

function ResponseStatus({ status }: { status: number }) {
  return (
    <Badge
      tone={status >= 500 ? 'danger' : status >= 400 ? 'warning' : 'success'}
      className="font-mono"
    >
      {status}
    </Badge>
  )
}

function RequestInfo({ request: r }: { request: SendRequestDetail }) {
  const app = useQuery(applicationQuery(r.application_id))
  return (
    <KeyValueList
      labelWidth="7rem"
      className="text-[13px]"
      items={[
        {
          label: 'Application',
          value: (
            <Link
              to="/applications/$appId"
              params={{ appId: r.application_id }}
              className="text-brass hover:underline"
            >
              {app.data?.name ?? r.application_id}
            </Link>
          ),
        },
        {
          label: 'Event',
          hideEmpty: true,
          value: r.event_key ? (
            r.event_type_id ? (
              <Link
                to="/event-types/$eventTypeId"
                params={{ eventTypeId: r.event_type_id }}
                className="font-mono text-xs text-brass hover:underline"
              >
                {r.event_key}
              </Link>
            ) : (
              <span className="font-mono text-xs">{r.event_key}</span>
            )
          ) : undefined,
        },
        { label: 'Kind', value: KIND_LABELS[r.kind] },
        {
          label: 'Credential',
          hideEmpty: true,
          value: r.credential_id ? (
            <Credential appId={r.application_id} id={r.credential_id} />
          ) : undefined,
        },
        {
          label: 'Idempotency key',
          value: r.idempotency_key ? (
            <span className="font-mono text-xs break-all">{r.idempotency_key}</span>
          ) : (
            <span className="text-ink-3">None</span>
          ),
        },
        {
          label: 'Body hash',
          hideEmpty: true,
          value: r.body_hash ? (
            <span className="font-mono text-[11px] break-all text-ink-2">{r.body_hash}</span>
          ) : undefined,
        },
        { label: 'Received', value: formatDateTime(r.created_at) },
        { label: 'Kept until', value: formatDateTime(r.expires_at) },
        {
          label: 'Metadata',
          hideEmpty: true,
          value: Object.keys(r.metadata ?? {}).length ? (
            <JsonViewer value={r.metadata} defaultExpandDepth={1} maxHeight={200} />
          ) : undefined,
        },
      ]}
    />
  )
}

/**
 * The API key a request authenticated with, by name and prefix. Falls back to
 * the raw ID for workload identities, deleted keys, or roles that can't list keys.
 */
function Credential({ appId, id }: { appId: string; id: string }) {
  const keys = useQuery({
    ...apiKeysQuery(appId, true),
    enabled: id.startsWith('key_'),
    retry: false,
  })
  const key = keys.data?.find((k) => k.id === id)
  if (!key) return <span className="font-mono text-xs break-all">{id}</span>
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Link
        to="/applications/$appId/keys"
        params={{ appId }}
        className="text-brass hover:underline"
        title={id}
      >
        {key.name}
      </Link>
      <span className="font-mono text-xs text-ink-3">{key.prefix}…</span>
    </span>
  )
}

function PayloadCard({ request: r }: { request: SendRequestDetail }) {
  const { can } = usePermissions()
  const payload = useMemo(() => r.payload, [r.payload])
  return (
    <Card>
      <CardHeader title="Payload" />
      <CardBody>
        {!can('view_message_bodies') ? (
          <p className="text-sm text-ink-2">Your role can’t view payloads.</p>
        ) : payload != null ? (
          <JsonViewer value={payload} defaultExpandDepth={3} maxHeight={560} />
        ) : (
          <EmptyState
            compact
            icon={Archive}
            title="Payload not stored"
            description="It was purged under the application’s retention policy, or the request had none."
          />
        )}
      </CardBody>
    </Card>
  )
}
